import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  ApiError,
  fetchMyProfile,
  submitOnboarding,
  type Profile,
  type SpifPayoutType,
} from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useToast } from "@/components/ui/toast";
import { FormError } from "@/components/ui/form-error";
import { CHANNEL_PARTNER } from "@/lib/role-labels";

export interface RewardsPreferencesDrawerProps {
  /** Open when true; false closes the drawer. */
  open: boolean;
  token: string;
  /** Fired when the drawer requests to close (overlay click / Esc / X / save). */
  onClose: () => void;
}

type OptIn = "yes" | "no" | "";
type PayoutChoice = Exclude<SpifPayoutType, "none"> | "";

/**
 * Rewards-model preference editor (Insights redesign D / CHP-487).
 *
 * A right Sheet mirroring the onboarding SPIF step (routes/onboarding/spif.tsx):
 * a Yes/No participation toggle and, when opted in, a Pooled-vs-Direct payout
 * choice. Pre-filled from the PO's profile (spif_opt_in + spif_payout_type) and
 * saved via the idempotent POST /auth/onboarding partial write — the same
 * endpoint the onboarding wizard uses, which the PO is always entitled to call.
 * Closes with a success toast on save.
 */
export function RewardsPreferencesDrawer({ open, token, onClose }: RewardsPreferencesDrawerProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const profileQuery = useQuery<Profile | null>({
    queryKey: ["profile", "me", token],
    queryFn: () => fetchMyProfile(token),
    enabled: !!token && open,
  });

  const [optIn, setOptIn] = React.useState<OptIn>("");
  const [payout, setPayout] = React.useState<PayoutChoice>("");
  const [formError, setFormError] = React.useState<string | null>(null);

  // Pre-fill from the profile each time the drawer opens (or the profile lands).
  React.useEffect(() => {
    if (!open) return;
    const p = profileQuery.data;
    if (!p) return;
    setOptIn(p.spif_opt_in === true ? "yes" : p.spif_opt_in === false ? "no" : "");
    setPayout(
      p.spif_payout_type === "pooled" || p.spif_payout_type === "direct" ? p.spif_payout_type : "",
    );
    setFormError(null);
  }, [open, profileQuery.data]);

  const save = useMutation({
    mutationFn: () => {
      const opt = optIn === "yes";
      const payout_type: SpifPayoutType = opt ? (payout as SpifPayoutType) : "none";
      return submitOnboarding(token, { spif_opt_in: opt, spif_payout_type: payout_type });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile", "me"] });
      toast({ variant: "success", description: "Reward preferences updated." });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 422) {
        setFormError("That choice isn't valid. Pick one of the options above.");
        return;
      }
      setFormError("Couldn't save your choice. Try again.");
    },
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    if (optIn === "") {
      setFormError("Choose Yes or No to continue.");
      return;
    }
    if (optIn === "yes" && payout === "") {
      setFormError("Choose how your bonus rewards should be distributed.");
      return;
    }
    save.mutate();
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        // CHP-1061: standard drawer width (matches TeamMemberDrawer et al).
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="rewards-preferences-drawer"
      >
        <SheetHeader>
          <SheetTitle className="pr-8">Rewards Model</SheetTitle>
          <SheetDescription>
            Bonus rewards can be earned and paid as part of the ResetData {CHANNEL_PARTNER} Program.
          </SheetDescription>
        </SheetHeader>

        {profileQuery.isLoading ? (
          <p role="status" className="mt-6 text-sm text-muted-foreground">
            Loading your preferences…
          </p>
        ) : profileQuery.error ? (
          <p role="alert" className="mt-6 text-sm text-destructive">
            Couldn&apos;t load your reward preferences.
          </p>
        ) : profileQuery.data?.organization == null ? (
          // CHP-1175: the preference lives on the Organisation — an org-less
          // PO has nowhere to store it (the backend 409s the write), so render
          // an explanatory state instead of a form that can't save.
          <p className="mt-6 text-sm text-muted-foreground" data-testid="rewards-orgless-notice">
            Rewards preferences are set for your whole organisation. Your account isn&apos;t linked
            to an organisation yet — a ResetData administrator can link it for you, after which you
            can choose your rewards model here.
          </p>
        ) : (
          <form
            onSubmit={onSubmit}
            aria-busy={save.isPending}
            noValidate
            className="mt-6 space-y-6"
          >
            <fieldset className="space-y-2">
              <legend className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                Would you like to participate in our rewards program? *
              </legend>
              <label className="flex items-start gap-3 rounded-md border p-3">
                <input
                  type="radio"
                  name="opt-in"
                  value="yes"
                  checked={optIn === "yes"}
                  onChange={() => setOptIn("yes")}
                  className="mt-1"
                  data-testid="rewards-opt-in-yes"
                />
                <span>
                  <span className="font-medium">Yes</span>
                  <span className="block text-xs text-muted-foreground">
                    We would like to participate.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 rounded-md border p-3">
                <input
                  type="radio"
                  name="opt-in"
                  value="no"
                  checked={optIn === "no"}
                  onChange={() => {
                    setOptIn("no");
                    setPayout("");
                  }}
                  className="mt-1"
                  data-testid="rewards-opt-in-no"
                />
                <span>
                  <span className="font-medium">No</span>
                  <span className="block text-xs text-muted-foreground">
                    We would not like to participate.
                  </span>
                </span>
              </label>
            </fieldset>

            {optIn === "yes" && (
              <fieldset className="space-y-2">
                <legend className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  How would you like your bonus rewards distributed? *
                </legend>
                <label className="flex items-start gap-3 rounded-md border p-3">
                  <input
                    type="radio"
                    name="payout"
                    value="pooled"
                    checked={payout === "pooled"}
                    onChange={() => setPayout("pooled")}
                    className="mt-1"
                    data-testid="rewards-payout-pooled"
                  />
                  <span className="font-medium">
                    Pooled as an organisation for management to distribute
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-md border p-3">
                  <input
                    type="radio"
                    name="payout"
                    value="direct"
                    checked={payout === "direct"}
                    onChange={() => setPayout("direct")}
                    className="mt-1"
                    data-testid="rewards-payout-direct"
                  />
                  <span className="font-medium">Paid directly to the individual</span>
                </label>
              </fieldset>
            )}

            {formError && <FormError>{formError}</FormError>}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={save.isPending}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={save.isPending}
                loadingLabel="Saving…"
                className="flex-1"
                data-testid="rewards-save"
              >
                Save preferences
              </Button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
